import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	OutlinedInput,
	Stack,
	TextField,
} from '@mui/material';
import { FC, useEffect } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { FormField } from '../../form/FormField';
import { Add, DeleteOutline } from '@mui/icons-material';
import { graphql } from '../../gql';
import { useApiMutation } from '../../query/hooks';
import { useFieldArray } from 'react-hook-form';
import { useModalContext } from '../../modal/ModalContext';
import { OOD_COLORS_LIST } from '../../types/colors';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const personaDetail_createPersonaBlock = graphql(`
	mutation personaDetail_createPersonaBlock(
		$name: String!
		$description: String
		$color: String!
		$items: [PersonaBlockCreateItemsEntityRelationInput!]
		$personaId: UUID!
		$order: Int!
	) {
		createPersonaBlock(
			data: {
				name: $name
				description: $description
				color: $color
				items: $items
				order: $order
				persona: { connect: { id: $personaId } }
			}
		) {
			ok
			errorMessage
		}
	}
`);

const createPersonaBlockModalSchema = z.object({
	personaId: z.string(),
	amountOfBlocks: z.string().transform(Number),
});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.persona.title.detail.block.error.empty-name' })),
		description: z.string().nullable(),
		items: z
			.array(
				z.object({
					id: z.string().nullable().optional(),
					name: z.string(),
				}),
			)
			.nullable(),
	});

type Props = z.infer<typeof createPersonaBlockModalSchema>;

type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

const PersonaBlockCreate: FC<Props> = ({ personaId, amountOfBlocks }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: createFormSchema(intl),
	});

	const { formState, control, reset, register, handleSubmit } = form;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'items',
	});

	useEffect(() => {
		reset({
			name: '',
			description: '',
			items: [],
		});
	}, [reset]);

	const { mutate: createBlock } = useApiMutation(personaDetail_createPersonaBlock, {
		onSuccess: async (result) => {
			mutationResponseHandler({
				result: result.createPersonaBlock.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'persona.detail.block.create.success' }),
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.createPersonaBlock.errorMessage,
				openSnackbar,
			});
		},
	});

	const onSubmit = (data: FormValues) => {
		const items = data.items?.map((item) => ({ create: { name: item.name } })) ?? [];
		const randomColor = OOD_COLORS_LIST[Math.floor(Math.random() * OOD_COLORS_LIST.length)];
		createBlock(
			{ name: data.name, description: data.description, color: randomColor, personaId, items: items, order: amountOfBlocks },
			{ onSuccess: closeModal },
		);
	};

	return (
		<form noValidate onSubmit={handleSubmit(onSubmit)}>
			<DialogTitle>
				<FormattedMessage id={'form.persona.detail.block.title.create'} />
			</DialogTitle>
			<Divider />
			<DialogContent>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Stack gap={3}>
							<FormField
								form={form}
								name={'name'}
								label={intl.formatMessage({ id: 'form.persona.title.detail.block.name' })}
								render={(props) => <OutlinedInput {...props} />}
							/>
							<FormField
								form={form}
								name={'description'}
								label={intl.formatMessage({ id: 'form.persona.title.detail.block.description' })}
								render={(props) => <TextField {...props} rows={5} multiline />}
							/>
							<Stack gap={1}>
								<InputLabel sx={{ fontWeight: 500 }}>
									<FormattedMessage id={'form.persona.title.detail.block.property-label'} />
								</InputLabel>
								<Stack gap={1}>
									{fields.map((item, index) => (
										<TextField
											{...register(`items.${index}.name`)}
											name={`items.${index}.name`}
											key={index}
											InputProps={{
												endAdornment: (
													<IconButton onClick={() => remove(index)}>
														<DeleteOutline />
													</IconButton>
												),
											}}
										/>
									))}
								</Stack>
							</Stack>
							<Stack direction={'row'} display={'flex'}>
								<Button variant={'text'} onClick={() => append({ name: '' })} startIcon={<Add sx={{ width: '18px', height: '18px' }} />}>
									<FormattedMessage id={'form.persona.title.detail.block.property-add'} />
								</Button>
							</Stack>
						</Stack>
					</Grid>

					{formState.errors.root != null && (
						<Grid item xs={12}>
							<FormHelperText error>{formState.errors.root.message}</FormHelperText>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<Divider />
			<DialogActions sx={{ p: 2.5 }}>
				<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
					<Button color={'secondary'} onClick={closeModal}>
						<FormattedMessage id={'common.cancel'} />
					</Button>
					<Button type={'submit'} variant={'contained'} disabled={formState.isSubmitting}>
						<FormattedMessage id={'common.create'} />
					</Button>
				</Stack>
			</DialogActions>
		</form>
	);
};

export { PersonaBlockCreate, createPersonaBlockModalSchema };
